import React from 'react'
import { createPortal } from 'react-dom'
import {
  Routes,
  Route,
  useNavigate,
  useParams,
  Navigate,
} from 'react-router-dom'
import {
  LayoutDashboard,
  Database,
  Search,
  Clock,
  Loader2,
  CalendarPlus,
  PlayCircle,
  CheckCircle2,
  AlertCircle,
  SearchX,
  Timer,
  Package,
  FileStack,
  Download,
  ImageIcon,
  Copy,
  PlusCircle,
  X,
  ArrowUpDown,
  ChevronDown,
  Menu,
  Filter,
} from 'lucide-react'
import { RowsPerPageDropdown } from './components/RowsPerPageDropdown'
import { useAuth } from './hooks/useAuth'
import { useLoadingBar } from './contexts/LoadingBarContext'
import {
  authFetch,
  getApiBase,
  fetchMatchingStatus,
  type BatchSummary,
} from './lib/api'
import { LoginPage } from './pages/LoginPage'
import { ProtectedRoute } from './components/ProtectedRoute'
import DashboardOverviewPage from './pages/DashboardOverviewPage'
import SettingsPage from './pages/SettingsPage'
import ProfilePage from './pages/ProfilePage'
import UploadBatchModal from './components/dashboard/UploadBatchModal'
import { Sidebar } from './components/shared/Sidebar'
import { Pagination } from './components/shared/Pagination'

interface Product {
  id: number
  batch_id: number
  zoro_no: string
  upc_code: string
  zoro_brand_name: string
  zoro_image_name?: string
  availability_status: string
  is_failed?: boolean
  last_searched_at?: string
  created_at: string
  matches?: Match[]
}

interface UploadBatch {
  id: number
  filename: string
  total_products: number
  status: string
  created_at: string
  started_at?: string | null
  completed_at?: string | null
  matchedProducts?: number
  /** From API: products processed so far (for Completed/Remaining metrics). */
  processedCount?: number
  /** From API: products that failed during matching. */
  failedCount?: number
}

interface BatchMatchingStatus {
  batchId: number
  status: 'running' | 'completed' | 'error' | 'stopped'
  total: number
  processedCount: number
  matchedCount: number
  /** Count of products that failed during matching (from API). */
  failedCount?: number
  currentlyProcessingId: number | null
  /** When batching: product ids in current listing call (all show "Scraping..."). */
  currentlyProcessingIds?: number[]
  processedIds: number[]
  startedAt: number
  lastError?: string
}

interface Match {
  id: number
  product_id: number
  match_index: number
  asin: string
  brand: string
  title: string
  customer_reviews: string
  global_ratings: number
  bsr: string
  sellers_json: string
  brand_match: boolean
  upc_match: boolean
  upc: string
  Product?: Product
}

function CopyableText({
  text,
  truncate = true,
  maxWidth = '100px',
}: Readonly<{
  text: string
  truncate?: boolean
  maxWidth?: string
}>) {
  const [copied, setCopied] = React.useState(false)
  const [hover, setHover] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  const doCopy = () => {
    if (!text || text === '-') return
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    doCopy()
  }

  if (!text || text === '-') return <span className='text-slate-400'>–</span>

  const popup =
    hover && triggerRef.current
      ? (() => {
          const rect = triggerRef.current.getBoundingClientRect()
          return (
            <div
              className='fixed z-[9999] pointer-events-none'
              style={{
                left: rect.left,
                bottom: window.innerHeight - rect.top + 8,
              }}
            >
              <div className='relative bg-slate-800 text-white px-4 py-3 rounded-xl shadow-2xl shadow-slate-900/30 min-w-[220px] max-w-[320px] border border-slate-700/50'>
                <div className='absolute -bottom-2 left-4 w-4 h-4 bg-slate-800 border-r border-b border-slate-700/50 rotate-45' />
                <div className='relative space-y-2'>
                  <p className='text-sm font-semibold leading-snug break-words pr-2'>
                    {text}
                  </p>
                  <div className='flex items-center gap-2 pt-1 border-t border-slate-600/60'>
                    <Copy size={14} className='text-emerald-400 shrink-0' />
                    <span className='text-xs font-medium text-slate-300'>
                      Click to copy
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        })()
      : null

  return (
    <>
      <button
        type='button'
        ref={triggerRef}
        onClick={handleCopy}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className='relative cursor-pointer active:scale-[0.98] transition-transform text-left bg-transparent border-0 p-0'
        title='Click to copy'
      >
        <div
          className={`${truncate ? 'truncate' : ''} text-[13px] leading-snug text-slate-800 hover:text-primary font-medium transition-colors`}
          style={truncate ? { maxWidth } : undefined}
        >
          {text}
        </div>
        {copied && (
          <div className='absolute top-0 right-0 -translate-y-full bg-emerald-600 text-white text-xs font-medium px-2 py-1 rounded-lg shadow-lg z-[100]'>
            Copied!
          </div>
        )}
      </button>
      {popup != null && createPortal(popup, document.body)}
    </>
  )
}

function ImageWithCopyUrl({ url }: Readonly<{ url: string }>) {
  const [copied, setCopied] = React.useState(false)

  const hasValidUrl =
    url &&
    !/^nan$/i.test(url) &&
    (url.startsWith('http') || url.startsWith('//'))

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!hasValidUrl) return
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // 🔥 SIZE CONTROL HERE
  const sizeClasses = 'w-24 h-24'

  if (!hasValidUrl) {
    return (
      <div
        className={`${sizeClasses} rounded-xl bg-slate-100 flex items-center justify-center`}
      >
        <ImageIcon size={40} className='text-slate-400' />
      </div>
    )
  }

  return (
    <div
      className={`relative group ${sizeClasses} rounded-xl overflow-hidden border border-slate-200 hover:border-primary/40 transition-colors`}
    >
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        className='block w-full h-full'
      >
        <img
          src={url}
          alt=''
          className='w-full h-full object-contain'
          onError={e => {
            ;(e.target as HTMLImageElement).style.display = 'none'
          }}
        />
      </a>

      <button
        type='button'
        onClick={handleCopy}
        className='absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer'
        title='Copy image URL'
      >
        {copied ? (
          <span className='text-xs font-semibold text-white'>Copied!</span>
        ) : (
          <span className='flex items-center gap-1 text-xs font-semibold text-white'>
            <Copy size={14} />
            Copy URL
          </span>
        )}
      </button>
    </div>
  )
}

const API = getApiBase()

function formatBatchDate(d: string | null | undefined): string {
  return d
    ? new Date(d).toLocaleString(undefined, {
        dateStyle: 'short',
        timeStyle: 'medium',
      })
    : '–'
}

function formatSellersJson(sellersJson: string | null | undefined): string {
  try {
    const sellers = JSON.parse(sellersJson ?? '[]') as Array<{
      seller_name?: string
      name?: string
    }>
    const names = sellers
      .map(s => s.seller_name ?? s.name)
      .filter(Boolean) as string[]
    const seen = new Set<string>()
    const unique = names.filter(n => {
      const k = String(n).trim().toLowerCase()
      if (k === '' || seen.has(k)) return false
      seen.add(k)
      return true
    })
    return unique.join(', ')
  } catch {
    return '-'
  }
}

function BatchTimelineCard({
  batch,
  currentBatchStatus,
}: Readonly<{
  batch: UploadBatch | undefined
  currentBatchStatus: BatchMatchingStatus | undefined
}>) {
  const isThisBatchProcessing = currentBatchStatus?.status === 'running'
  const startedAt = currentBatchStatus?.startedAt
  const elapsedMs = startedAt == null ? 0 : Date.now() - startedAt
  const remaining =
    isThisBatchProcessing &&
    currentBatchStatus &&
    currentBatchStatus.total > 0 &&
    currentBatchStatus.processedCount > 0
      ? Math.max(
          0,
          ((currentBatchStatus.total - currentBatchStatus.processedCount) /
            currentBatchStatus.processedCount) *
            elapsedMs
        )
      : null
  let etaStr: string
  if (remaining == null) {
    etaStr = '–'
  } else {
    etaStr =
      remaining >= 60000
        ? `~${Math.round(remaining / 60000)} min remaining`
        : `~${Math.round(remaining / 1000)} sec remaining`
  }
  let etaValue = '–'
  if (isThisBatchProcessing) etaValue = etaStr
  else if (
    currentBatchStatus?.status === 'stopped' ||
    batch?.status === 'stopped'
  )
    etaValue = 'Stopped – click Resume to continue'
  else if (batch?.completed_at) etaValue = 'Done'
  const isDone = !!batch?.completed_at
  const isStopped =
    currentBatchStatus?.status === 'stopped' || batch?.status === 'stopped'

  return (
    <div className='rounded-2xl border border-border bg-card overflow-hidden shadow-lg shadow-black/5'>
      <div className='px-6 pt-6 pb-4'>
        <h3 className='text-lg font-semibold flex items-center gap-2'>
          <div className='p-1.5 rounded-lg bg-primary/10'>
            <Clock size={18} className='text-primary' />
          </div>
          Timeline
        </h3>
      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border/50'>
        <div className='p-5 bg-slate-50/50 hover:bg-slate-50/80 transition-colors'>
          <div className='flex items-center gap-2 mb-2'>
            <div className='p-1.5 rounded-md bg-blue-500/10'>
              <CalendarPlus size={14} className='text-blue-600' />
            </div>
            <span className='text-muted-foreground text-xs font-semibold uppercase tracking-wider'>
              Created
            </span>
          </div>
          <span className='font-mono text-sm font-medium text-foreground'>
            {batch ? formatBatchDate(batch.created_at) : '–'}
          </span>
        </div>
        <div className='p-5 bg-slate-50/50 hover:bg-slate-50/80 transition-colors'>
          <div className='flex items-center gap-2 mb-2'>
            <div className='p-1.5 rounded-md bg-indigo-500/10'>
              <PlayCircle size={14} className='text-indigo-600' />
            </div>
            <span className='text-muted-foreground text-xs font-semibold uppercase tracking-wider'>
              Started
            </span>
          </div>
          <span className='font-mono text-sm font-medium text-foreground'>
            {batch ? formatBatchDate(batch.started_at ?? undefined) : '–'}
          </span>
        </div>
        <div className='p-5 bg-slate-50/50 hover:bg-slate-50/80 transition-colors'>
          <div className='flex items-center gap-2 mb-2'>
            <div className='p-1.5 rounded-md bg-purple-500/10'>
              <CheckCircle2 size={14} className='text-purple-600' />
            </div>
            <span className='text-muted-foreground text-xs font-semibold uppercase tracking-wider'>
              Completed
            </span>
          </div>
          <span className='font-mono text-sm font-medium text-foreground'>
            {batch ? formatBatchDate(batch.completed_at ?? undefined) : '–'}
          </span>
        </div>
        <div className='p-5 bg-slate-50/50 hover:bg-slate-50/80 transition-colors'>
          <div className='flex items-center gap-2 mb-2'>
            <div className='p-1.5 rounded-md bg-amber-500/10'>
              <Timer size={14} className='text-amber-600' />
            </div>
            <span className='text-muted-foreground text-xs font-semibold uppercase tracking-wider'>
              Est. time to complete
            </span>
          </div>
          {isDone && (
            <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/15 text-emerald-700 text-sm font-semibold'>
              <CheckCircle2 size={14} />
              Done
            </span>
          )}
          {!isDone && isStopped && (
            <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-700 text-sm font-medium'>
              {etaValue}
            </span>
          )}
          {!isDone && !isStopped && (
            <span className='font-mono text-sm font-medium text-foreground'>
              {etaValue}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function DashboardContent() {
  const navigate = useNavigate()
  const { batchId: batchIdParam } = useParams<{ batchId?: string }>()
  const parsed = Number.parseInt(batchIdParam ?? '', 10)
  const initialBatchId = Number.isNaN(parsed) ? null : parsed
  const { user } = useAuth()
  const loadingBar = useLoadingBar()
  const [products, setProducts] = React.useState<Product[]>([])
  const [batches, setBatches] = React.useState<UploadBatch[]>([])
  const [batchesLoaded, setBatchesLoaded] = React.useState(false)
  const [selectedBatchId, setSelectedBatchId] = React.useState<number | null>(
    initialBatchId
  )
  const [pagination, setPagination] = React.useState({
    page: 1,
    totalPages: 1,
    total: 0,
  })
  const [matches, setMatches] = React.useState<Match[]>([])
  const [loading, setLoading] = React.useState(true)

  const [itemsPerPage, setItemsPerPage] = React.useState(50)
  const [batchStatuses, setBatchStatuses] = React.useState<
    BatchMatchingStatus[]
  >([])

  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false)
  const [batchNotFound, setBatchNotFound] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [sortBy, setSortBy] = React.useState<
    'createdAt' | 'id' | 'zoroNo' | 'upc' | 'brand' | 'availability'
  >('createdAt')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc')
  const [sortDropdownOpen, setSortDropdownOpen] = React.useState(false)
  const sortDropdownRef = React.useRef<HTMLDivElement>(null)
  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false)
  const statusDropdownRef = React.useRef<HTMLDivElement>(null)
  const [productStatusFilter, setProductStatusFilter] = React.useState<
    'all' | 'completed' | 'failed' | 'remaining' | 'not_found'
  >('all')
  const [visibleCompletedBatchIds, setVisibleCompletedBatchIds] =
    React.useState<Set<number>>(new Set())
  const [sidebarOpen, setSidebarOpen] = React.useState(false)

  const currentPageRef = React.useRef(pagination.page)

  React.useEffect(() => {
    currentPageRef.current = pagination.page
  }, [pagination.page])

  const fetchBatches = React.useCallback(async () => {
    loadingBar.start()
    try {
      const response = await authFetch(`${API}/api/products/batches`)
      if (response.ok) {
        const data = await response.json()

        // Backwards compatibility: handle both old array response and new
        // `{ batches, pagination }` dashboard response by normalizing to the
        // original `UploadBatch` shape used in this component.
        let normalized: UploadBatch[] = []

        if (Array.isArray(data)) {
          normalized = data
        } else if (Array.isArray(data.batches)) {
          normalized = data.batches.map((b: BatchSummary) => {
            const status =
              b.status === 'failed' ? 'error' : (b.status ?? 'pending')
            return {
              id: b.id,
              filename: b.fileName ?? b.batchName ?? '',
              total_products: b.totalProducts ?? 0,
              status,
              created_at: b.createdAt ?? new Date().toISOString(),
              started_at: b.startedAt ?? null,
              completed_at: b.completedAt ?? null,
              matchedProducts: b.matchedProducts,
              processedCount: b.processedCount,
              failedCount: b.failedCount,
            }
          })
        }

        setBatches(normalized)
        const first = normalized[0]
        if (first && selectedBatchId === null) {
          setSelectedBatchId(first.id)
        }
      }
    } catch {
      // Failed to fetch batches
    } finally {
      setBatchesLoaded(true)
      loadingBar.done()
    }
  }, [selectedBatchId, loadingBar])

  const fetchProducts = React.useCallback(
    async (
      page: number = 1,
      batchId: number | null = selectedBatchId,
      limit: number = itemsPerPage,
      search?: string,
      sort?: { sortBy: typeof sortBy; sortOrder: typeof sortOrder },
      backgroundRefresh: boolean = false,
      statusFilter?: 'all' | 'completed' | 'failed' | 'remaining' | 'not_found'
    ) => {
      const activeSearch = search ?? searchTerm
      const activeSortBy = sort?.sortBy ?? sortBy
      const activeSortOrder = sort?.sortOrder ?? sortOrder
      const activeStatus = statusFilter ?? productStatusFilter
      if (!backgroundRefresh) setLoading(true)
      try {
        const url = new URL(`${API}/api/products`)
        if (batchId) url.searchParams.append('batch_id', batchId.toString())
        url.searchParams.append('page', page.toString())
        url.searchParams.append('limit', limit.toString())
        if (activeSearch) url.searchParams.append('search', activeSearch)
        if (activeStatus !== 'all')
          url.searchParams.append('status', activeStatus)
        url.searchParams.append('sortBy', activeSortBy)
        url.searchParams.append('sortOrder', activeSortOrder)

        const response = await authFetch(url.toString())
        if (response.ok) {
          const data = await response.json()
          const productList = Array.isArray(data.products) ? data.products : []
          setProducts(productList)
          setPagination({
            page: data.currentPage,
            totalPages: data.totalPages,
            total: data.total,
          })
        }
      } catch {
        // Failed to fetch products; error is non-fatal (UI shows empty list)
      } finally {
        if (!backgroundRefresh) setLoading(false)
      }
    },
    [
      selectedBatchId,
      itemsPerPage,
      searchTerm,
      productStatusFilter,
      sortBy,
      sortOrder,
    ]
  )

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(target)
      ) {
        setSortDropdownOpen(false)
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(target)
      ) {
        setStatusDropdownOpen(false)
      }
    }
    if (sortDropdownOpen || statusDropdownOpen)
      document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [sortDropdownOpen, statusDropdownOpen])

  const fetchProductsRef = React.useRef(fetchProducts)
  React.useEffect(() => {
    fetchProductsRef.current = fetchProducts
  }, [fetchProducts])

  const batchStatusesRef = React.useRef(batchStatuses)
  React.useEffect(() => {
    batchStatusesRef.current = batchStatuses
  }, [batchStatuses])

  const fetchMatches = React.useCallback(async (batchId?: number | null) => {
    try {
      const url = new URL(`${API}/api/products/matches`)
      if (batchId != null)
        url.searchParams.append('batch_id', batchId.toString())
      const response = await authFetch(url.toString())
      if (response.ok) {
        setMatches(await response.json())
      }
    } catch {
      // Failed to fetch matches
    }
  }, [])

  const fetchBatchStatus = React.useCallback(async () => {
    try {
      const data = await fetchMatchingStatus()
      const statuses = (data.batches as unknown as BatchMatchingStatus[]) ?? []

      const prevStatuses = batchStatusesRef.current
      setBatchStatuses(statuses)

      // If the currently viewed batch is running, refresh the product list in background (no table loader)
      const currentBatch = statuses.find(b => b.batchId === selectedBatchId)
      if (currentBatch?.status === 'running') {
        fetchProductsRef.current(
          currentPageRef.current,
          selectedBatchId,
          itemsPerPage,
          undefined,
          undefined,
          true
        )
      }

      const wasRunning = prevStatuses.some(
        b => b.batchId === selectedBatchId && b.status === 'running'
      )
      if (
        wasRunning &&
        currentBatch &&
        (currentBatch.status === 'completed' ||
          currentBatch.status === 'stopped')
      ) {
        if (currentBatch.status === 'completed') {
          setVisibleCompletedBatchIds(prev => {
            const next = new Set(prev)
            next.add(currentBatch.batchId)
            return next
          })
          setTimeout(() => {
            setVisibleCompletedBatchIds(prev => {
              const next = new Set(prev)
              next.delete(currentBatch.batchId)
              return next
            })
          }, 15000)
        }
        fetchBatches()
        fetchProductsRef.current(
          currentPageRef.current,
          selectedBatchId,
          itemsPerPage,
          undefined,
          undefined,
          false
        )
        fetchMatches(selectedBatchId)
      }
    } catch {
      // Failed to fetch batch status; non-fatal
    }
  }, [selectedBatchId, itemsPerPage, fetchBatches, fetchMatches])

  // Track first products load after batches so we don't show global bar twice on batch page load
  const initialProductsLoadRef = React.useRef(true)
  // Fetch products when batches load (immediate) or when search/batch changes (debounced for search only).
  // We use fetchProductsRef so this effect does NOT depend on fetchProducts; otherwise changing
  // itemsPerPage would change fetchProducts identity and re-run this effect, causing a second
  // fetch when user changes row size (dropdown already fetches once). So itemsPerPage is omitted on purpose.
  React.useEffect(() => {
    if (!batchesLoaded) return

    setLoading(true)
    const isInitialLoad = initialProductsLoadRef.current
    if (isInitialLoad) initialProductsLoadRef.current = false
    const delay = isInitialLoad ? 0 : 400

    const timer = setTimeout(() => {
      fetchProductsRef.current(1, selectedBatchId, itemsPerPage, searchTerm)
    }, delay)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- itemsPerPage omitted to avoid double fetch on row-size change (dropdown fetches once)
  }, [searchTerm, selectedBatchId, batchesLoaded])

  // Initial data fetch
  React.useEffect(() => {
    fetchBatches()
    setMatches([])
    fetchMatches(selectedBatchId)
    fetchBatchStatus()
  }, [fetchBatches, fetchMatches, selectedBatchId, fetchBatchStatus])

  // Polling logic
  React.useEffect(() => {
    const isAnyBatchRunning = batchStatuses.some(b => b.status === 'running')
    const isCurrentBatchRunning = batchStatuses.some(
      b => b.batchId === selectedBatchId && b.status === 'running'
    )

    if (!isAnyBatchRunning && !isCurrentBatchRunning) return

    const interval = setInterval(() => {
      fetchBatchStatus()
    }, 5000)
    return () => clearInterval(interval)
  }, [batchStatuses, selectedBatchId, fetchBatchStatus])

  // If URL batchId does not exist, flag for 404-style UI
  React.useEffect(() => {
    if (!batchesLoaded) return
    if (selectedBatchId == null) {
      setBatchNotFound(Boolean(batchIdParam))
      return
    }

    const exists = batches.some(b => b.id === selectedBatchId)
    setBatchNotFound(!exists)
  }, [batchesLoaded, batches, selectedBatchId, batchIdParam])

  const uniqueMatchedCount = React.useMemo(() => {
    return new Set(matches.map(m => m.product_id)).size
  }, [matches])

  return (
    <div className='flex h-screen bg-background text-foreground font-sans overflow-hidden'>
      <Sidebar
        active='dashboard'
        onNavigate={section => {
          if (section === 'dashboard') navigate('/dashboard')
          else if (section === 'settings') navigate('/settings')
          else if (section === 'profile') navigate('/profile')
        }}
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <main className='flex-1 overflow-auto relative min-w-0'>
        <div className='absolute inset-0 bg-gradient-to-tr from-blue-100/50 via-background to-purple-100/50 pointer-events-none' />

        <header className='h-auto min-h-[4.5rem] border-b border-border flex flex-row items-center justify-between gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 bg-white/90 backdrop-blur-md sticky top-0 z-10 transition-all shadow-sm'>
          <div className='flex items-center gap-2 sm:gap-3 min-w-0'>
            <button
              type='button'
              onClick={() => setSidebarOpen(true)}
              className='lg:hidden p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 text-muted-foreground hover:text-foreground shrink-0'
              aria-label='Open menu'
            >
              <Menu size={24} />
            </button>
            <div className='min-w-0'>
              <h1 className='text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-foreground to-muted-foreground truncate'>
                Dashboard
              </h1>
              <p className='text-muted-foreground text-xs sm:text-sm truncate'>
                Welcome back, {user?.name ?? user?.email ?? 'User'}
              </p>
            </div>
          </div>

          <div className='flex items-center gap-2 sm:gap-4 shrink-0'>
            <button
              type='button'
              onClick={() => setIsUploadModalOpen(true)}
              className='inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold shadow-lg hover:shadow-xl hover:from-blue-500 hover:to-purple-500 transition-all'
            >
              <PlusCircle size={18} />
              <span className='hidden sm:inline'>Upload New Batch</span>
              <span className='sm:hidden'>Upload</span>
            </button>
          </div>
        </header>

        <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 sm:space-y-8 relative z-0'>
          {batchNotFound && (
            <div className='rounded-2xl border border-amber-200 bg-amber-50/80 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm'>
              <div className='flex items-center justify-center h-10 w-10 rounded-xl bg-amber-100 text-amber-700 shrink-0'>
                <Database className='h-5 w-5' />
              </div>
              <div className='flex-1'>
                <h2 className='text-sm font-semibold text-amber-900'>
                  Batch not found
                </h2>
                <p className='text-xs text-amber-800 mt-0.5'>
                  The batch ID in the URL does not exist. Select another batch
                  from the table below or go back to the main dashboard.
                </p>
              </div>
              <button
                type='button'
                onClick={() => navigate('/dashboard')}
                className='px-3 py-2 text-xs font-medium rounded-lg border border-amber-300 text-amber-900 bg-white hover:bg-amber-50 transition-colors shrink-0'
              >
                Back to dashboard
              </button>
            </div>
          )}

          <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
            {(() => {
              const currentStatus = batchStatuses.find(
                b => b.batchId === selectedBatchId
              )
              const batch = batches.find(b => b.id === selectedBatchId)

              const displayTotal =
                currentStatus?.total ??
                batch?.total_products ??
                pagination.total
              const displayMatched = currentStatus
                ? currentStatus.matchedCount
                : (batch?.matchedProducts ?? uniqueMatchedCount)
              const totalForMetrics =
                currentStatus?.total ?? batch?.total_products ?? 0
              const processedCount =
                currentStatus?.processedCount ?? batch?.processedCount ?? 0
              const failedCount =
                currentStatus?.failedCount ?? batch?.failedCount ?? 0
              const matchedCount =
                currentStatus?.matchedCount ?? batch?.matchedProducts ?? 0
              const notFoundCount = Math.max(
                0,
                processedCount - matchedCount - failedCount
              )
              const remaining = Math.max(0, totalForMetrics - processedCount)

              return (
                <>
                  <StatCard
                    label='Total Products'
                    value={displayTotal.toLocaleString()}
                    icon={<Database className='h-5 w-5' />}
                  />
                  <StatCard
                    label='Matched Products'
                    value={displayMatched.toLocaleString()}
                    icon={<Search className='h-5 w-5' />}
                  />
                  <StatCard
                    label='Coverage'
                    value={
                      displayTotal
                        ? (() => {
                            const pct = (displayMatched / displayTotal) * 100
                            // For small percentages, show 2 decimals
                            // For percentages < 10%, show 1 decimal
                            if (pct > 0 && pct < 0.1)
                              return pct.toFixed(2) + '%'
                            if (pct > 0 && pct < 10) return pct.toFixed(1) + '%'
                            return Math.round(pct) + '%'
                          })()
                        : '0%'
                    }
                    icon={<LayoutDashboard className='h-5 w-5' />}
                  />
                  {(currentStatus ?? batch) && totalForMetrics > 0 && (
                    <>
                      <StatCard
                        label='Completed'
                        value={processedCount.toLocaleString()}
                        icon={<CheckCircle2 className='h-5 w-5' />}
                      />
                      <StatCard
                        label='Failed'
                        value={failedCount.toLocaleString()}
                        icon={<AlertCircle className='h-5 w-5' />}
                      />
                      <StatCard
                        label='Not Found'
                        value={notFoundCount.toLocaleString()}
                        icon={<SearchX className='h-5 w-5' />}
                      />
                      <StatCard
                        label='Remaining'
                        value={Math.max(0, remaining).toLocaleString()}
                        icon={<Clock className='h-5 w-5' />}
                      />
                    </>
                  )}
                </>
              )
            })()}
          </div>

          {selectedBatchId != null &&
            (() => {
              const currentBatchStatus = batchStatuses.find(
                b => b.batchId === selectedBatchId
              )
              const isQueued =
                currentBatchStatus?.status === 'running' &&
                (currentBatchStatus?.processedCount ?? 0) === 0 &&
                batchStatuses.some(
                  b =>
                    b.batchId !== selectedBatchId &&
                    b.status === 'running' &&
                    (b.processedCount ?? 0) > 0
                )
              return isQueued ? (
                <div className='rounded-2xl border border-primary/20 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 p-5 flex items-center gap-4 shadow-sm'>
                  <div className='h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center shrink-0'>
                    <Clock className='h-6 w-6 text-primary' />
                  </div>
                  <p className='text-sm text-foreground'>
                    <span className='font-semibold'>
                      This batch is in the queue.
                    </span>{' '}
                    It will start automatically once the currently running batch
                    process completes.
                  </p>
                </div>
              ) : null
            })()}

          {selectedBatchId != null &&
            (() => {
              const currentBatchStatus = batchStatuses.find(
                b => b.batchId === selectedBatchId
              )
              const hasError =
                currentBatchStatus &&
                (currentBatchStatus.status === 'error' ||
                  currentBatchStatus.status === 'stopped') &&
                currentBatchStatus.lastError

              if (!hasError) return null

              return (
                <div className='rounded-2xl border border-red-200 bg-red-50/90 px-5 py-4 flex items-start gap-3 shadow-sm'>
                  <div className='mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-red-100 text-red-700 shrink-0'>
                    <AlertCircle className='h-5 w-5' />
                  </div>
                  <div className='space-y-1'>
                    <p className='text-sm font-semibold text-red-900'>
                      Matching stopped due to an external API error.
                    </p>
                    <p className='text-xs text-red-800 leading-snug'>
                      {currentBatchStatus.lastError}
                    </p>
                  </div>
                </div>
              )
            })()}

          {selectedBatchId != null && (
            <BatchTimelineCard
              batch={batches.find(b => b.id === selectedBatchId)}
              currentBatchStatus={batchStatuses.find(
                b => b.batchId === selectedBatchId
              )}
            />
          )}

          {selectedBatchId != null &&
            (() => {
              const currentBatchStatus = batchStatuses.find(
                b => b.batchId === selectedBatchId
              )
              const batch = batches.find(b => b.id === selectedBatchId)
              const total =
                currentBatchStatus?.total ?? batch?.total_products ?? 0
              const processedCount =
                currentBatchStatus?.processedCount ?? batch?.processedCount ?? 0
              const isStopped =
                currentBatchStatus?.status === 'stopped' ||
                batch?.status === 'stopped'
              const isCompleted =
                currentBatchStatus?.status === 'completed' ||
                batch?.status === 'completed'
              const isRunning = currentBatchStatus?.status === 'running'

              const showProgress =
                (isRunning ||
                  isStopped ||
                  (isCompleted &&
                    currentBatchStatus &&
                    visibleCompletedBatchIds.has(selectedBatchId))) &&
                total > 0

              if (showProgress) {
                const pct = (processedCount / total) * 100
                let progressMessage: string
                if (isCompleted && currentBatchStatus?.status === 'completed') {
                  progressMessage = `Scan Complete! ${currentBatchStatus.processedCount} products processed. Found ${currentBatchStatus.matchedCount} matches.`
                } else if (isStopped) {
                  progressMessage = `Stopped at ${processedCount} of ${total} products processed. Click Resume to continue.`
                } else {
                  progressMessage = `Searching for matches: ${processedCount} of ${total} products processed...`
                }
                return (
                  <div className='rounded-2xl border border-border bg-card p-6 shadow-lg shadow-black/5 relative group'>
                    {isCompleted &&
                      currentBatchStatus?.status === 'completed' && (
                        <button
                          onClick={() => {
                            setVisibleCompletedBatchIds(prev => {
                              const next = new Set(prev)
                              next.delete(selectedBatchId)
                              return next
                            })
                          }}
                          className='absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 text-muted-foreground transition-colors'
                          title='Dismiss'
                        >
                          <X size={16} />
                        </button>
                      )}
                    <div className='flex justify-between items-center mb-2'>
                      <span className='text-sm font-semibold text-primary'>
                        Progress
                      </span>
                      <span className='text-xs font-mono'>
                        {Math.round(pct)}%
                      </span>
                    </div>
                    <div className='h-3.5 bg-slate-200 rounded-full overflow-hidden'>
                      <div
                        className={`h-full transition-all duration-500 ease-out rounded-full ${
                          isStopped
                            ? 'bg-amber-500'
                            : 'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className='text-xs text-muted-foreground mt-2'>
                      {progressMessage}
                    </p>
                  </div>
                )
              }
              return null
            })()}

          <div className='rounded-2xl border border-border bg-card overflow-hidden shadow-lg shadow-black/5 min-w-0'>
            <div className='px-4 sm:px-6 pt-5 pb-6 sm:pt-6 sm:pb-7 border-b border-border bg-gradient-to-r from-slate-50 to-white min-h-[7.5rem]'>
              {/* Row 1: Title + count + batch (left) | Search + Export (right) */}
              <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-center gap-3 sm:gap-4 min-w-0'>
                  <div className='p-1.5 rounded-lg bg-primary/10 shrink-0'>
                    <Database size={18} className='text-primary' />
                  </div>
                  <div className='min-w-0'>
                    <h3 className='text-base sm:text-lg font-semibold'>
                      Products & Matches
                    </h3>
                    <div className='flex items-center gap-2 sm:gap-3 mt-1 flex-wrap'>
                      <span className='text-xs sm:text-sm text-muted-foreground font-medium'>
                        {pagination.total}{' '}
                        {pagination.total === 1 ? 'item' : 'items'} found
                      </span>
                      {selectedBatchId && (
                        <span className='inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 border rounded bg-gradient-to-r from-primary/15 to-indigo-500/15 text-primary font-semibold text-xs border border-primary/20'>
                          <FileStack size={12} />
                          Batch #{selectedBatchId}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Row 2: Filter options (Status + Sort) below */}
              <div className='flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-slate-200/80'>
                <div className='relative' ref={statusDropdownRef}>
                  <button
                    type='button'
                    onClick={() => setStatusDropdownOpen(o => !o)}
                    className='inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 transition-all'
                  >
                    <Filter size={16} />
                    {`Status: ${
                      {
                        all: 'All',
                        completed: 'Matched',
                        failed: 'Failed',
                        remaining: 'Remaining',
                        not_found: 'Not Found',
                      }[productStatusFilter]
                    }`}
                    <ChevronDown
                      size={14}
                      className={statusDropdownOpen ? 'rotate-180' : ''}
                    />
                  </button>
                  {statusDropdownOpen && (
                    <div className='absolute left-0 top-full mt-1.5 w-48 py-2 rounded-xl border border-slate-200 bg-white shadow-lg z-20'>
                      {[
                        { value: 'all' as const, label: 'All' },
                        { value: 'completed' as const, label: 'Matched' },
                        { value: 'failed' as const, label: 'Failed' },
                        { value: 'remaining' as const, label: 'Remaining' },
                        { value: 'not_found' as const, label: 'Not Found' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type='button'
                          onClick={() => {
                            setProductStatusFilter(opt.value)
                            setStatusDropdownOpen(false)
                            fetchProducts(
                              1,
                              selectedBatchId,
                              itemsPerPage,
                              searchTerm,
                              undefined,
                              false,
                              opt.value
                            )
                          }}
                          className={`w-full px-3 py-2 text-left text-sm font-medium transition-colors ${
                            productStatusFilter === opt.value
                              ? 'bg-primary/10 text-primary'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className='relative' ref={sortDropdownRef}>
                  <button
                    type='button'
                    onClick={() => setSortDropdownOpen(o => !o)}
                    className='inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 transition-all'
                  >
                    <ArrowUpDown size={16} />
                    Sort
                    <ChevronDown
                      size={14}
                      className={sortDropdownOpen ? 'rotate-180' : ''}
                    />
                  </button>
                  {sortDropdownOpen && (
                    <div className='absolute left-0 top-full mt-1.5 w-56 py-2 rounded-xl border border-slate-200 bg-white shadow-lg z-20'>
                      <div className='px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide'>
                        Sort by
                      </div>
                      {[
                        { value: 'createdAt' as const, label: 'Date' },
                        { value: 'id' as const, label: 'ID' },
                        { value: 'zoroNo' as const, label: 'Zoro No' },
                        { value: 'upc' as const, label: 'UPC' },
                        { value: 'brand' as const, label: 'Brand' },
                        {
                          value: 'availability' as const,
                          label: 'Availability',
                        },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type='button'
                          onClick={() => {
                            setSortBy(opt.value)
                            setSortDropdownOpen(false)
                            fetchProducts(
                              1,
                              selectedBatchId,
                              itemsPerPage,
                              searchTerm,
                              {
                                sortBy: opt.value,
                                sortOrder,
                              }
                            )
                          }}
                          className={`w-full px-3 py-2 text-left text-sm font-medium transition-colors ${
                            sortBy === opt.value
                              ? 'bg-primary/10 text-primary'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                      <div className='border-t border-slate-100 mt-2 pt-2'>
                        <div className='px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide'>
                          Order
                        </div>
                        <div className='flex gap-1 px-2'>
                          <button
                            type='button'
                            onClick={() => {
                              setSortOrder('asc')
                              setSortDropdownOpen(false)
                              fetchProducts(
                                1,
                                selectedBatchId,
                                itemsPerPage,
                                searchTerm,
                                {
                                  sortBy,
                                  sortOrder: 'asc',
                                }
                              )
                            }}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                              sortOrder === 'asc'
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-slate-50 text-slate-600'
                            }`}
                          >
                            Asc
                          </button>
                          <button
                            type='button'
                            onClick={() => {
                              setSortOrder('desc')
                              setSortDropdownOpen(false)
                              fetchProducts(
                                1,
                                selectedBatchId,
                                itemsPerPage,
                                searchTerm,
                                {
                                  sortBy,
                                  sortOrder: 'desc',
                                }
                              )
                            }}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                              sortOrder === 'desc'
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-slate-50 text-slate-600'
                            }`}
                          >
                            Desc
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Search + Export pushed to right */}
                <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto sm:ml-auto mt-2 sm:mt-0'>
                  <div className='relative w-full sm:w-56 shrink min-w-0'>
                    <Search
                      size={16}
                      className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                    />
                    <input
                      type='text'
                      placeholder='Search UPC or Zoro No...'
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          fetchProducts(
                            1,
                            selectedBatchId,
                            itemsPerPage,
                            searchTerm
                          )
                        }
                      }}
                      className='pl-10 pr-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 w-full transition-all'
                    />
                    {searchTerm && (
                      <button
                        onClick={() => {
                          setSearchTerm('')
                          fetchProducts(1, selectedBatchId, itemsPerPage, '')
                        }}
                        className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      const url = new URL(`${API}/api/products/export`)
                      if (selectedBatchId)
                        url.searchParams.append(
                          'batch_id',
                          selectedBatchId.toString()
                        )
                      url.searchParams.append('format', 'csv')
                      if (productStatusFilter !== 'all')
                        url.searchParams.append('status', productStatusFilter)
                      if (searchTerm)
                        url.searchParams.append('search', searchTerm)
                      const res = await authFetch(url.toString())
                      if (!res.ok) return
                      const blob = await res.blob()
                      const a = document.createElement('a')
                      a.href = URL.createObjectURL(blob)
                      a.download = `matches-${selectedBatchId ?? 'all'}.csv`
                      a.click()
                      URL.revokeObjectURL(a.href)
                    }}
                    className='inline-flex items-center justify-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-md hover:shadow-lg shrink-0 w-max self-start sm:self-auto sm:w-auto'
                  >
                    <Download size={14} className='sm:w-4 sm:h-4' />
                    Export to CSV
                  </button>
                </div>
              </div>
            </div>
            <div className='p-0'>
              {loading ? (
                <div className='flex flex-col items-center justify-center gap-4 py-20 px-6 min-h-[220px] rounded-b-xl border border-slate-300 border-t-0 bg-slate-50/90'>
                  <Loader2
                    size={44}
                    className='animate-spin text-primary'
                    strokeWidth={2.5}
                  />
                  <p className='text-sm font-semibold text-slate-600'>
                    Loading products...
                  </p>
                </div>
              ) : (
                <>
                  <div className='overflow-auto max-h-[calc(100vh-14rem)] border border-slate-300 shadow-sm'>
                    <table className='w-full text-sm text-left border-separate border-spacing-0'>
                      <thead className='bg-slate-50'>
                        <tr className='border-b-2 border-slate-300'>
                          <th
                            colSpan={8}
                            className='sticky top-0 z-10 px-4 py-3.5 text-center bg-slate-100 font-semibold text-slate-700 border-b-2 border-r border-slate-300'
                          >
                            Original Product
                          </th>
                          <th
                            colSpan={10}
                            className='sticky top-0 z-10 px-4 py-3.5 text-center bg-blue-50 font-semibold text-blue-800 border-b-2 border-r border-slate-300'
                          >
                            Match 1 (Primary)
                          </th>
                          <th
                            colSpan={10}
                            className='sticky top-0 z-10 px-4 py-3.5 text-center bg-purple-50 font-semibold text-purple-800 border-b-2 border-r border-slate-300'
                          >
                            Match 2
                          </th>
                          <th
                            colSpan={10}
                            className='sticky top-0 z-10 px-4 py-3.5 text-center bg-pink-50 font-semibold text-pink-800 border-b-2 border-slate-300'
                          >
                            Match 3
                          </th>
                        </tr>
                        <tr className='text-slate-700 text-[13px] font-semibold'>
                          <th className='sticky top-[3.0rem] z-[9] bg-slate-100 px-4 py-3 min-w-[50px] border-b border-r border-slate-300'>
                            No.
                          </th>
                          <th className='sticky top-[3.0rem] z-[9] bg-slate-100 px-4 py-3 min-w-[100px] border-b border-r border-slate-300'>
                            Product ID
                          </th>
                          <th className='sticky top-[3.0rem] z-[9] bg-slate-100 px-4 py-3 min-w-[110px] border-b border-r border-slate-300'>
                            Zoro No
                          </th>
                          <th className='sticky top-[3.0rem] z-[9] bg-slate-100 px-4 py-3 min-w-[110px] border-b border-r border-slate-300'>
                            UPC
                          </th>
                          <th className='sticky top-[3.0rem] z-[9] bg-slate-100 px-4 py-3 min-w-[140px] border-b border-r border-slate-300'>
                            Brand
                          </th>
                          <th className='sticky top-[3.0rem] z-[9] bg-slate-100 px-4 py-3 min-w-[100px] border-b border-r border-slate-300'>
                            <span className='inline-flex items-center gap-1'>
                              <ImageIcon size={12} />
                              Image
                            </span>
                          </th>
                          <th className='sticky top-[3.0rem] z-[9] bg-slate-100 px-4 py-3 min-w-[120px] border-b border-r border-slate-300'>
                            Availability
                          </th>
                          <th className='sticky top-[3.0rem] z-[9] bg-slate-100 px-4 py-3 min-w-[100px] border-b border-r border-slate-300'>
                            Status
                          </th>

                          {['bg-blue-50', 'bg-purple-50', 'bg-pink-50'].map(
                            bg => (
                              <React.Fragment key={bg}>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[110px] border-b border-r border-slate-300`}
                                >
                                  ASIN
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[140px] border-b border-r border-slate-300`}
                                >
                                  Brand
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[200px] border-b border-r border-slate-300`}
                                >
                                  Title
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[130px] border-b border-r border-slate-300`}
                                >
                                  Reviews
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[90px] border-b border-r border-slate-300`}
                                >
                                  Ratings
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[120px] border-b border-r border-slate-300`}
                                >
                                  UPC (Amazon)
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[90px] border-b border-r border-slate-300`}
                                >
                                  BSR
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[200px] border-b border-r border-slate-300`}
                                >
                                  Competitors
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[90px] border-b border-r border-slate-300`}
                                >
                                  Brand Match
                                </th>
                                <th
                                  className={`sticky top-[3.0rem] z-[9] ${bg} px-4 py-3 min-w-[90px] border-b border-r border-slate-300`}
                                >
                                  UPC Match
                                </th>
                              </React.Fragment>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody className='relative z-0 bg-white'>
                        {products.length === 0 ? (
                          <tr>
                            <td colSpan={38} className='px-6 py-16 text-center'>
                              <div className='sticky left-0 w-[calc(100vw-20rem)] max-w-full flex justify-center'>
                                <div className='flex flex-col items-center gap-3'>
                                  <div className='p-4 rounded-2xl bg-slate-100'>
                                    <Package
                                      size={40}
                                      className='text-slate-400'
                                    />
                                  </div>
                                  <p className='text-base font-semibold text-foreground'>
                                    No products found
                                  </p>
                                  <p className='text-sm text-muted-foreground'>
                                    Select a batch or upload products to see
                                    data here.
                                  </p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          products.map((product, rowIdx) => {
                            const pMatches = (product.matches ?? []).sort(
                              (a, b) => a.match_index - b.match_index
                            )

                            const isMatched = pMatches.length > 0
                            const currentBatchStatus = batchStatuses.find(
                              b => b.batchId === selectedBatchId
                            )
                            const isThisBatchProcessing =
                              currentBatchStatus?.status === 'running'
                            const isProcessing =
                              isThisBatchProcessing &&
                              (currentBatchStatus?.currentlyProcessingId ===
                                product.id ||
                                (
                                  currentBatchStatus?.currentlyProcessingIds ??
                                  []
                                ).includes(product.id))
                            const isProcessed = (
                              currentBatchStatus?.processedIds ?? []
                            ).some(id => String(id) === String(product.id))

                            let statusBadge: React.ReactNode
                            if (isMatched) {
                              statusBadge = (
                                <span className='inline-flex items-center gap-1.5 px-3 py-1 border rounded bg-emerald-500/15 text-emerald-700 text-xs font-semibold border border-emerald-500/60'>
                                  <CheckCircle2 size={12} />
                                  Completed
                                </span>
                              )
                            } else if (isProcessing) {
                              statusBadge = (
                                <span className='inline-flex items-center gap-1.5 px-2.5 py-1 border rounded bg-amber-500/15 text-amber-700 text-xs font-semibold border border-amber-500/60 animate-pulse'>
                                  <Loader2 size={12} className='animate-spin' />
                                  Scraping...
                                </span>
                              )
                            } else if (
                              isProcessed ||
                              (isThisBatchProcessing &&
                                currentBatchStatus?.status === 'completed') ||
                              product.last_searched_at
                            ) {
                              if (product.is_failed === true) {
                                statusBadge = (
                                  <span className='inline-flex items-center gap-1.5 px-2.5 py-1 border rounded bg-red-600/15 text-red-700 text-xs font-semibold border border-red-600/60'>
                                    <X size={12} />
                                    Failed/Error
                                  </span>
                                )
                              } else {
                                statusBadge = (
                                  <span className='inline-flex items-center gap-1.5 px-2.5 py-1 border rounded bg-red-500/15 text-red-600 text-xs font-semibold border border-red-500/60'>
                                    Not Found
                                  </span>
                                )
                              }
                            } else {
                              statusBadge = (
                                <span className='inline-flex items-center gap-1.5 px-2.5 py-1 border rounded bg-blue-500/15 text-blue-600 text-xs font-semibold border border-blue-500/60'>
                                  Waiting...
                                </span>
                              )
                            }

                            return (
                              <tr
                                key={product.id}
                                className={`transition-colors hover:bg-blue-50/50 ${
                                  rowIdx % 2 === 0
                                    ? 'bg-white'
                                    : 'bg-slate-50/60'
                                }`}
                              >
                                <td className='px-4 py-3.5 text-[13px] text-slate-500 font-medium border-b border-r border-slate-200'>
                                  {(pagination.page - 1) * itemsPerPage +
                                    rowIdx +
                                    1}
                                </td>
                                <td className='px-4 py-3.5 text-[13px] text-slate-500 font-medium border-b border-r border-slate-200'>
                                  #{product.id}
                                </td>
                                <td className='px-4 py-3.5 text-[13px] border-b border-r border-slate-200'>
                                  <CopyableText
                                    text={product.zoro_no}
                                    truncate={false}
                                  />
                                </td>
                                <td className='px-4 py-3.5 text-[13px] border-b border-r border-slate-200'>
                                  <CopyableText
                                    text={product.upc_code}
                                    truncate={false}
                                  />
                                </td>
                                <td className='px-4 py-3.5 font-medium border-b border-r border-slate-200'>
                                  <CopyableText
                                    text={product.zoro_brand_name}
                                    maxWidth='140px'
                                  />
                                </td>
                                <td className='px-4 py-3.5 overflow-hidden align-top border-b border-r border-slate-200'>
                                  <ImageWithCopyUrl
                                    url={product.zoro_image_name || ''}
                                  />
                                </td>
                                <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                  <span className='inline-flex items-center px-2.5 py-1 border rounded text-xs font-semibold bg-emerald-500/15 text-emerald-700 border border-emerald-500/60'>
                                    {product.availability_status || 'In Stock'}
                                  </span>
                                </td>
                                <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                  {statusBadge}
                                </td>

                                {[1, 2, 3].map(index => {
                                  const m = pMatches.find(
                                    match => match.match_index === index
                                  )
                                  if (!m)
                                    return new Array(10)
                                      .fill(null)
                                      .map((_, cellIdx) => (
                                        <td
                                          key={`empty-${product.id}-${index}-${cellIdx}`}
                                          className='px-4 py-3.5 text-slate-400 italic text-[13px] border-b border-r border-slate-200'
                                        >
                                          –
                                        </td>
                                      ))

                                  return (
                                    <React.Fragment key={index}>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <CopyableText text={m.asin} />
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <CopyableText
                                          text={m.brand}
                                          maxWidth='140px'
                                        />
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <CopyableText
                                          text={m.title}
                                          truncate
                                          maxWidth='200px'
                                        />
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <CopyableText
                                          text={m.customer_reviews || 'N/A'}
                                        />
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <CopyableText
                                          text={
                                            m.global_ratings?.toString() || '0'
                                          }
                                        />
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <CopyableText text={m.upc || '-'} />
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <CopyableText text={m.bsr || '-'} />
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <CopyableText
                                          text={formatSellersJson(
                                            m.sellers_json
                                          )}
                                          maxWidth='200px'
                                        />
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <span
                                          className={`inline-flex items-center px-2 py-0.5 border rounded text-xs font-semibold ${
                                            m.brand_match
                                              ? 'bg-emerald-500/15 text-emerald-700 border border-emerald-500/20'
                                              : 'bg-red-500/15 text-red-600 border border-red-500/20'
                                          }`}
                                        >
                                          {m.brand_match ? 'Yes' : 'No'}
                                        </span>
                                      </td>
                                      <td className='px-4 py-3.5 border-b border-r border-slate-200'>
                                        <span
                                          className={`inline-flex items-center px-2 py-0.5 border rounded text-xs font-semibold ${
                                            m.upc_match
                                              ? 'bg-emerald-500/15 text-emerald-700 border border-emerald-500/20'
                                              : 'bg-red-500/15 text-red-600 border border-red-500/20'
                                          }`}
                                        >
                                          {m.upc_match ? 'Yes' : 'No'}
                                        </span>
                                      </td>
                                    </React.Fragment>
                                  )
                                })}
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className='flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-3 sm:px-4 py-3 border border-t-0 border-slate-300 bg-slate-50'>
                    <RowsPerPageDropdown
                      value={itemsPerPage}
                      direction='up'
                      onChange={val => {
                        setItemsPerPage(val)
                        fetchProducts(1, selectedBatchId, val)
                      }}
                    />
                    <Pagination
                      currentPage={pagination.page}
                      totalPages={pagination.totalPages}
                      onPageChange={page => fetchProducts(page)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <UploadBatchModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadSuccess={batchId => {
            if (Number.isNaN(batchId)) {
              fetchProducts()
            } else {
              setSelectedBatchId(batchId)
              fetchProducts(1, batchId, itemsPerPage)
            }
            fetchBatches()
          }}
          onUploadError={() => {}}
        />
      </main>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
}: Readonly<{
  label: string
  value: string
  icon: React.ReactNode
}>) {
  return (
    <div className='p-6 rounded-none border border-border bg-card shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 transition-all duration-200 group overflow-hidden relative flex items-center justify-between'>
      <div className='absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/5 to-transparent rounded-bl-full' />
      <div className='relative flex-shrink-0 p-3 rounded-none bg-gradient-to-br from-blue-100 to-blue-50 text-blue-700 group-hover:from-blue-200 group-hover:to-blue-100 transition-colors'>
        {icon}
      </div>
      <div className='relative text-right'>
        <div className='inline-block text-center space-y-0.5'>
          <h4 className='text-3xl font-bold tracking-tight text-foreground'>
            {value}
          </h4>
          <p className='text-sm font-medium text-muted-foreground'>{label}</p>
        </div>
      </div>
    </div>
  )
}

function App() {
  return (
    <Routes>
      <Route path='/login' element={<LoginPage />} />
      <Route
        path='/dashboard'
        element={
          <ProtectedRoute>
            <DashboardOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/settings'
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/profile'
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/dashboard/batch/:batchId'
        element={
          <ProtectedRoute>
            <DashboardContent />
          </ProtectedRoute>
        }
      />
      <Route path='/' element={<Navigate to='/dashboard' replace />} />
      <Route
        path='*'
        element={
          <ProtectedRoute>
            <DashboardOverviewPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

export default App
